import React from 'react';

interface BrandInitialsProps {
  name: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * A component that displays a brand's initials in a stylized container
 * when no logo is available
 */
export default function BrandInitials({ name, size = 'md', className = '' }: BrandInitialsProps) {
  // Generate initials from brand name (up to 2 characters)
  const getInitials = (brandName: string): string => {
    if (!brandName) return '?';

    // Split by spaces and get first letters of first two words
    const words = brandName.split(' ').filter(word => word.length > 0);
    if (words.length === 0) return '?';

    if (words.length === 1) {
      // If only one word, use first letter or first two letters if short
      return words[0].substring(0, words[0].length < 6 ? 6 : 1).toUpperCase();
    }

    // Otherwise use first letter of first two words
    return (words[0][0] + words[1][0]).toUpperCase();
  };

  // Generate a consistent color based on the brand name
  const getColorClass = (brandName: string): string => {
    const colors = [
      'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
      'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200',
      'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200',
      'bg-rose-100 text-rose-800 dark:bg-rose-900 dark:text-rose-200',
      'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
      'bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200',
      'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-200',
    ];

    // Use string hash to select a consistent color
    let hash = 0;
    for (let i = 0; i < brandName.length; i++) {
      hash = brandName.charCodeAt(i) + ((hash << 5) - hash);
    }

    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-16 h-16',
    lg: 'w-24 h-24',
  };

  // Adjust text size based on content length and container size
  const getTextSizeClass = (text: string, size: 'sm' | 'md' | 'lg'): string => {
    // For CJK characters, use smaller text size
    const isCJK =
      /[\u3000-\u303f\u3040-\u309f\u30a0-\u30ff\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\uff66-\uff9f]/.test(
        text
      );

    if (isCJK) {
      switch (size) {
        case 'sm':
          return 'text-xs';
        case 'md':
          return 'text-sm';
        case 'lg':
          return 'text-lg';
        default:
          return 'text-base';
      }
    }

    // For regular characters
    switch (size) {
      case 'sm':
        return text.length > 1 ? 'text-[0.6rem]' : 'text-xs';
      case 'md':
        return text.length > 1 ? 'text-sm' : 'text-base';
      case 'lg':
        return text.length > 1 ? 'text-lg' : 'text-xl';
      default:
        return 'text-base';
    }
  };

  const initials = getInitials(name);
  const colorClass = getColorClass(name);
  const textSizeClass = getTextSizeClass(initials, size);

  return (
    <div
      className={`flex items-center justify-center font-bold rounded-lg transition-colors duration-300 ${colorClass} ${sizeClasses[size]} ${className} flex flex-row items-center`}
      aria-label={`${name} logo placeholder`}
    >
      <span className={`${textSizeClass} leading-none`}>{initials}</span>
    </div>
  );
}
